/**
 * OrderTrackingViewModel
 * Platform-agnostic ViewModel for order tracking functionality
 * Supports both pickup and delivery order tracking with real-time updates
 */

import { useCallback, useState, useEffect, useMemo } from 'react'
import type { IPlatformServices } from '../contracts'
import { safeGetLogger } from '@restaurant/logging'

import type {
  Order,
  SheetContentPickupOrderStatusProps,
  SheetContentDeliveryOrderStatusProps,
  OrderStatusProps,
} from '@restaurant/core'
import { mapOrderStateToStatusFlow } from '@restaurant/core'
import type {
  IOrderTrackingService,
  DriverLocation,
  OrderTrackingStatus,
  RestaurantContact,
  OrderTrackingMapConfig,
} from '../contracts/IOrderTrackingService'
import { OrderNoLongerAvailableError } from '../contracts/OrderTrackingErrors'

// Re-export types for easier imports
export type {
  IOrderTrackingService,
  DriverLocation,
  OrderTrackingStatus,
  RestaurantContact,
  OrderTrackingMapConfig,
} from '../contracts/IOrderTrackingService'

/**
 * Dependencies for OrderTrackingViewModel
 */
export interface OrderTrackingViewModelDependencies {
  orderTrackingService?: IOrderTrackingService
}

/**
 * OrderTrackingViewModel state interface
 */
export interface OrderTrackingViewModelState {
  order: Order | null
  trackingStatus: OrderTrackingStatus | null
  driverLocation: DriverLocation | null
  restaurantContact: RestaurantContact | null
  loading: boolean
  error: string | null
  isTrackingAvailable: boolean
  timeRemaining: string | null
  orderStatusProps: OrderStatusProps
  mapConfig: OrderTrackingMapConfig | null
}

/**
 * OrderTrackingViewModel actions interface
 */
export interface OrderTrackingViewModelActions {
  loadOrderTracking: (order: Order) => Promise<void>
  handleGetDirections: () => Promise<void>
  handleContactRestaurant: () => Promise<void>
  clearError: () => void
  refreshTracking: () => Promise<void>
  getPickupOrderStatusProps: () => Partial<SheetContentPickupOrderStatusProps>
  getDeliveryOrderStatusProps: () => Partial<SheetContentDeliveryOrderStatusProps>
}

/**
 * Combined OrderTrackingViewModel interface
 */
export interface OrderTrackingViewModel extends OrderTrackingViewModelState, OrderTrackingViewModelActions {}

/**
 * Platform-agnostic OrderTrackingViewModel factory
 * Uses dependency injection for platform-specific services
 */
export const createOrderTrackingViewModel = (
  _services: IPlatformServices,
  dependencies?: OrderTrackingViewModelDependencies
) => {
  return (): OrderTrackingViewModel => {
    const [order, setOrder] = useState<Order | null>(null)
    const [trackingStatus, setTrackingStatus] = useState<OrderTrackingStatus | null>(null)
    const [driverLocation, setDriverLocation] = useState<DriverLocation | null>(null)
    const [restaurantContact, setRestaurantContact] = useState<RestaurantContact | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [mapConfig, setMapConfig] = useState<OrderTrackingMapConfig | null>(null)

    const orderTrackingService = dependencies?.orderTrackingService

    /**
     * Check if tracking is available for the current order
     */
    const isTrackingAvailable = useMemo(() => {
      if (!order || !orderTrackingService) return false
      return orderTrackingService.isTrackingAvailable(order)
    }, [order, orderTrackingService])

    /**
     * Calculate time remaining until order completion
     */
    const timeRemaining = useMemo(() => {
      if (!trackingStatus?.estimatedCompletionTime || !orderTrackingService) return null
      return orderTrackingService.getTimeRemaining(trackingStatus.estimatedCompletionTime)
    }, [trackingStatus?.estimatedCompletionTime, orderTrackingService])

    /**
     * Generate props for OrderStatus component
     */
    const orderStatusProps = useMemo((): OrderStatusProps => {
      if (!trackingStatus) {
        return {
          orderStatus: 'Order Placed',
          statusText: 'Order Placed',
        }
      }

      const orderStatus = mapOrderStateToStatusFlow(trackingStatus.currentState, order?.isDelivery)
      const latestUpdate = trackingStatus.statusUpdates[trackingStatus.statusUpdates.length - 1]
      const statusText = latestUpdate?.message || orderStatus

      return {
        orderStatus,
        statusText,
      }
    }, [trackingStatus, order?.isDelivery])

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
      setError(null)
    }, [])

    /**
     * Load order tracking status and related data
     */
    const loadOrderTracking = useCallback(
      async (orderToTrack: Order) => {
        if (!orderTrackingService) return

        setLoading(true)
        setError(null)
        setOrder(orderToTrack)

        try {
          // Load tracking status
          const status = await orderTrackingService.getOrderTrackingStatus(orderToTrack.id)

          // The tracking service re-fetches the order from the API, which may
          // not include time estimates. Fall back to values from the order
          // object passed in (enriched by the checkout flow).
          if (!status.pickupTime && orderToTrack.pickupTimeEstimated) {
            status.pickupTime = orderTrackingService.formatTimeForDisplay(orderToTrack.pickupTimeEstimated)
          }
          if (!status.estimatedCompletionTime && orderToTrack.pickupTimeEstimated) {
            status.estimatedCompletionTime = orderToTrack.pickupTimeEstimated
          }
          if (!status.deliveryTimeRange && orderToTrack.dropoffTimeEstimated) {
            const endDate = new Date(new Date(orderToTrack.dropoffTimeEstimated).getTime() + 10 * 60 * 1000)
            status.deliveryTimeRange = {
              startTime: orderToTrack.dropoffTimeEstimated,
              endTime: endDate.toISOString(),
            }
          }

          setTrackingStatus(status)

          // Load restaurant contact info
          try {
            const contact = await orderTrackingService.getRestaurantContact(orderToTrack.merchantId)
            setRestaurantContact(contact)
          } catch (contactError) {
            safeGetLogger()?.warn('Failed to load restaurant contact', { domain: 'order', error: String(contactError) })
            setError(contactError instanceof Error ? contactError.message : 'Failed to load contact info')
          }

          // For delivery orders, load driver location
          if (orderToTrack.isDelivery) {
            try {
              const location = await orderTrackingService.getDriverLocation(orderToTrack.id)
              setDriverLocation(location)
            } catch (locationError) {
              safeGetLogger()?.warn('Failed to load driver location', { domain: 'order', error: String(locationError) })
              // Don't set error for driver location as it's not critical
            }
          }

          // Load map configuration
          try {
            const config = await orderTrackingService.getMapConfig(orderToTrack.id, orderToTrack.isDelivery)
            setMapConfig(config)
          } catch (mapError) {
            safeGetLogger()?.warn('Failed to load map config', { domain: 'order', error: String(mapError) })
          }
        } catch (err) {
          if (err instanceof OrderNoLongerAvailableError) {
            // Order completed / deleted / rotated — clear tracking silently.
            setTrackingStatus(null)
            return
          }
          safeGetLogger()?.error('Failed to load order tracking', { domain: 'order', error: String(err) })
          setError(err instanceof Error ? err.message : 'Failed to load tracking status')
        } finally {
          setLoading(false)
        }
      },
      [orderTrackingService]
    )

    /**
     * Refresh tracking data
     */
    const refreshTracking = useCallback(async () => {
      if (order) {
        await loadOrderTracking(order)
      }
    }, [order, loadOrderTracking])

    /**
     * Handle get directions action for pickup orders
     */
    const handleGetDirections = useCallback(async () => {
      if (!order || !orderTrackingService) return

      try {
        await orderTrackingService.getDirectionsToRestaurant(order.merchantId)
      } catch (err) {
        safeGetLogger()?.error('Failed to get directions', { domain: 'order', error: String(err) })
        setError(err instanceof Error ? err.message : 'Directions not available')
      }
    }, [order, orderTrackingService])

    /**
     * Handle contact restaurant action
     */
    const handleContactRestaurant = useCallback(async () => {
      if (!order || !orderTrackingService) return

      try {
        await orderTrackingService.contactRestaurant(order.merchantId)
      } catch (err) {
        safeGetLogger()?.error('Failed to contact restaurant', { domain: 'order', error: String(err) })
        setError(err instanceof Error ? err.message : 'Unable to contact restaurant')
      }
    }, [order, orderTrackingService])

    /**
     * Generate props for SheetContentPickupOrderStatus component
     */
    const getPickupOrderStatusProps = useCallback((): Partial<SheetContentPickupOrderStatusProps> => {
      if (!orderTrackingService || !trackingStatus) {
        return {
          pickupTime: '--:--',
          orderStatusProps,
          mapProps: { showMap: false },
          onGetDirections: handleGetDirections,
          onContactRestaurant: handleContactRestaurant,
        }
      }

      // Compute a time range matching Flutter: start – (start + 10 min)
      let pickupTime = '--:--'
      const rawPickupTime = trackingStatus.estimatedCompletionTime
      if (rawPickupTime) {
        const startFormatted = orderTrackingService.formatTimeForDisplay(rawPickupTime)
        const endDate = new Date(new Date(rawPickupTime).getTime() + 10 * 60 * 1000)
        const endFormatted = orderTrackingService.formatTimeForDisplay(endDate.toISOString())
        pickupTime = `${startFormatted} - ${endFormatted}`
      } else if (trackingStatus.pickupTime) {
        pickupTime = trackingStatus.pickupTime
      }

      return {
        pickupTime,
        orderStatusProps,
        mapProps: {
          showMap: true,
          backgroundColor: '#f5f5f5',
        },
        onGetDirections: handleGetDirections,
        onContactRestaurant: handleContactRestaurant,
        getDirectionsButtonProps: {
          children: 'Get Directions',
          variant: 'Secondary' as const,
        },
        contactRestaurantButtonProps: {
          children: 'Contact Restaurant',
          variant: 'Tertiary' as const,
        },
      }
    }, [orderTrackingService, trackingStatus, orderStatusProps, handleGetDirections, handleContactRestaurant])

    /**
     * Generate props for SheetContentDeliveryOrderStatus component
     */
    const getDeliveryOrderStatusProps = useCallback((): Partial<SheetContentDeliveryOrderStatusProps> => {
      if (!orderTrackingService || !trackingStatus) {
        return {
          deliveryTime: '--:-- - --:--',
          orderStatusProps,
          mapConfig: {},
          showContactButton: true,
          onContactPress: handleContactRestaurant,
        }
      }

      const deliveryTime = trackingStatus.deliveryTimeRange
        ? orderTrackingService.formatTimeForDisplay(trackingStatus.deliveryTimeRange)
        : '--:-- - --:--'

      return {
        deliveryTime,
        orderStatusProps,
        mapConfig: {
          backgroundColor: '#f5f5f5',
          customContent: driverLocation ? (
            `Driver location: ${driverLocation.latitude.toFixed(4)}, ${driverLocation.longitude.toFixed(4)}`
          ) : undefined,
        },
        showContactButton: true,
        onContactPress: handleContactRestaurant,
        onMapPress: () => {
          // Handle map interaction if needed
        },
        contactButtonProps: {
          children: 'Contact Restaurant',
        },
      }
    }, [orderTrackingService, trackingStatus, orderStatusProps, driverLocation, handleContactRestaurant])

    /**
     * Subscribe to real-time order updates
     */
    useEffect(() => {
      if (!order || !orderTrackingService) return

      const unsubscribeOrder = orderTrackingService.subscribeToOrderUpdates(
        order.id,
        (updatedStatus) => {
          setTrackingStatus(updatedStatus)
        }
      )

      let unsubscribeDriver: (() => void) | undefined

      // Subscribe to driver location updates for delivery orders
      if (order.isDelivery) {
        unsubscribeDriver = orderTrackingService.subscribeToDriverLocation(
          order.id,
          (location) => {
            setDriverLocation(location)
          }
        )
      }

      return () => {
        unsubscribeOrder()
        unsubscribeDriver?.()
      }
    }, [order, orderTrackingService])

    return {
      order,
      trackingStatus,
      driverLocation,
      restaurantContact,
      loading,
      error,
      isTrackingAvailable,
      timeRemaining,
      mapConfig,
      orderStatusProps,
      loadOrderTracking,
      handleGetDirections,
      handleContactRestaurant,
      clearError,
      refreshTracking,
      getPickupOrderStatusProps,
      getDeliveryOrderStatusProps,
    }
  }
}